import { Collection, getFirebase } from 'firestorter';

import envConfig from '../config';
import Collections from '../utils/collections';
import { LongSessionStage, WorkoutPublishStatus } from '../utils/longSession';
import { firestorePaths } from '../utils/paths';
import BaseDocument from './BaseDocument';
import Plan from './Plan';
import Package from './Package';

const FeatureId = {
  NUTRITION_PLAN: 'NUTRITION_PLAN',
  WORKOUT_VIDEO_FEEDBACK: 'WORKOUT_VIDEO_FEEDBACK',
  CHECK_IN_NOTIFICATION: 'CHECK_IN_NOTIFICATION',
  ASSISTANT_COACH: 'ASSISTANT_COACH',
  CHAT: 'CHAT',
  AI_FRIEND: 'AI_FRIEND',
};

const ProductType = {
  STANDARD: 'STANDARD',
  HYROX: 'HYROX',
  // Dual-written with the legacy `isChallengeProduct` boolean for old-client compat.
  CHALLENGE: 'CHALLENGE',
};

class Product extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.PRODUCT}/${id}`, opts);
  }

  get name() {
    return this.data.name;
  }

  get description() {
    return this.data.description;
  }

  get coach() {
    return this.data.coach;
  }

  /** @returns {string | undefined} The community members of this product are enrolled into. */
  get communityId() {
    return this.data.communityId;
  }

  get isS2Product() {
    return !!this.data.isS2Product;
  }

  get baseFee() {
    return this.data.baseFee;
  }

  get isHidden() {
    return !!this.data.isHidden;
  }

  get isDefault() {
    return !!this.data.isDefault;
  }

  get postPaymentForm() {
    return this.data.postPaymentForm;
  }

  // Pins a specific native PPF (forms doc id) to this product.
  get postPaymentFormId() {
    return this.data.postPaymentFormId;
  }

  get features() {
    return this.data.features || [];
  }

  /*
    The product's app look, set in CRX next to the coach's (`appCustomization.theme`). The mobile app shows it, instead
    of the coach's, to the clients it loads this product for. An empty value follows the coach's.
  */
  get appColorMode() {
    return this.data.theme?.colorMode || '';
  }

  // The preset theme, an `appCustomTheme` id.
  get appThemePreset() {
    return this.data.theme?.baseTheme || '';
  }

  // Overrides the preset's color, as the coach's primary color overrides theirs.
  get appPrimaryColor() {
    return this.data.theme?.colors?.base?.primary || '';
  }

  /**
   * Saves the product's app look. An empty value is removed, so it follows the coach's again.
   *
   * @param {{ colorMode: string, baseTheme: string, primaryColor: string }} appTheme - The look to save.
   * @return {Promise<void>}
   */
  setAppTheme({ colorMode, baseTheme, primaryColor }) {
    const deleteField = getFirebase().firestore.FieldValue.delete();
    return this.updateFields({
      'theme.colorMode': colorMode || deleteField,
      'theme.baseTheme': baseTheme || deleteField,
      'theme.colors.base.primary': primaryColor || deleteField,
    });
  }

  /*
    The product's own mobile app icon and in-app logo, set by admins in Products & Pricing beside the coach's in CRX
    (`appCustomization.appIconName` / `webAppIconRef`). The mobile app shows them instead of the coach's to the
    clients it brands with this product. An empty value follows the coach's.
  */
  get appIconName() {
    return this.data.appIconName || '';
  }

  get webAppIconRef() {
    return this.data.webAppIconRef || '';
  }

  /**
   * Saves the product's app icon and in-app logo. An empty value is removed, so it follows the coach's again.
   *
   * @param {{ appIconName: string, webAppIconRef: string }} appIcon - The icon name and the logo's storage reference.
   * @return {Promise<void>}
   */
  setAppIcon({ appIconName, webAppIconRef }) {
    const deleteField = getFirebase().firestore.FieldValue.delete();
    return this.updateFields({
      appIconName: appIconName.trim() || deleteField,
      webAppIconRef: webAppIconRef || deleteField,
    });
  }

  /**
   * Whether a feature is available to a client holding these products — the union,
   * since a client can hold one subscription per product. An S2 product grants everything.
   *
   * @param {Array<Product>} products - The client's products; missing ones already dropped.
   * @param {string} featureId - The feature to test.
   * @param {boolean} [defaultValue] - Answer when no product lists the feature.
   * @return {boolean}
   */
  static isFeatureAvailable(products, featureId, defaultValue = true) {
    if (!products.length) {
      return defaultValue;
    }
    return products.some((product) => product.isS2Product
      || product.isFeatureEnabled(featureId, defaultValue));
  }

  isFeatureEnabled = (featureId, defaultValue = true) => {
    const feature = this.features.find(({ id }) => id === featureId);

    // If the feature is not found, it is enabled by default.
    if (!feature) {
      return defaultValue;
    }
    return !!feature.enabled;
  }

  get isMealPlanEnabled() {
    return this.isFeatureEnabled(FeatureId.NUTRITION_PLAN);
  }

  get isVideoFeedBackEnabled() {
    return this.isFeatureEnabled(FeatureId.WORKOUT_VIDEO_FEEDBACK, false);
  }

  get isAssistantCoachEnabled() {
    return this.isFeatureEnabled(FeatureId.ASSISTANT_COACH, false);
  }

  get isChatEnabled() {
    return this.isFeatureEnabled(FeatureId.CHAT, false);
  }

  get isCheckInFeedBackEnabled() {
    return this.isFeatureEnabled(FeatureId.CHECK_IN_NOTIFICATION, false);
  }

  get isAiFriendEnabled() {
    return this.isFeatureEnabled(FeatureId.AI_FRIEND, false);
  }

  get isArchived() {
    return !!this.data.isArchived;
  }

  get isChallengeProduct() {
    return !!this.data.isChallengeProduct;
  }

  // Products created before product types existed have no type — treat as STANDARD.
  get type() {
    return this.data.type || ProductType.STANDARD;
  }

  /**
   * Whether this is a challenge product.
   * @return {boolean}
   */
  get isChallenge() {
    return this.type === ProductType.CHALLENGE || !!this.data.isChallengeProduct;
  }

  get isHyroxProduct() {
    return this.type === ProductType.HYROX;
  }

  setAsChallengeProduct() {
    return this.updateFields({
      isChallengeProduct: true,
      type: ProductType.CHALLENGE,
      challengePostPaymentForm: envConfig.challenges.postPaymentForm,
    });
  }

  unsetAsChallengeProduct() {
    return this.updateFields({
      isChallengeProduct: false,
      // Only reset the type when it's actually CHALLENGE — a Hyrox product toggling its
      // challenge flag off must stay HYROX.
      ...(this.type === ProductType.CHALLENGE ? { type: ProductType.STANDARD } : {}),
      challengePostPaymentForm: '',
    });
  }

  archive() {
    return this.updateFields({
      isArchived: true,
      archivedAt: new Date(),
    });
  }

  unarchive() {
    return this.updateFields({
      isArchived: false,
    });
  }

  /**
   * The welcome video new clients see once, after onboarding. Written only by the server.
   *
   * @returns {Object|undefined} `{ vimeoVideoId, hlsVideoUrl, videoUrl, thumbnailUrl,
   * durationSeconds, sourceStoragePath, optimizedStoragePath, readyAt }`
   */
  get welcomeVideo() {
    return this.data.welcomeVideo;
  }

  /**
   * A welcome video the long-session pipeline is still processing. The coach writes it to start an
   * upload; the server advances it, and deletes it when the video goes live as `welcomeVideo`.
   *
   * @returns {Object|undefined} `{ publishStatus, videoProcessing, sourceStoragePath, vimeoVideoId, ... }`
   */
  get welcomeVideoUpload() {
    return this.data.welcomeVideoUpload;
  }

  /**
   * Points the welcome video upload at a new source and puts it into its uploading state.
   *
   * The status moves with the path in one write: the server deletes a finalized upload unless it
   * reads PENDING at stage UPLOADING. Field by field rather than the whole map, so a retry keeps the
   * failed attempt's `vimeoVideoId`, which the server needs to delete that attempt's Vimeo video.
   *
   * @param {string} sourceStoragePath Where the video is about to be uploaded.
   * @param {string} uploadedBy The logged-in user, who may be a CAM acting for the owning coach.
   * @returns {Promise<void>}
   */
  startWelcomeVideoUpload(sourceStoragePath, uploadedBy) {
    return this.updateFields({
      'welcomeVideoUpload.sourceStoragePath': sourceStoragePath,
      'welcomeVideoUpload.publishStatus': WorkoutPublishStatus.PENDING,
      'welcomeVideoUpload.videoProcessing': {
        stage: LongSessionStage.UPLOADING,
        updatedAt: getFirebase().firestore.FieldValue.serverTimestamp(),
      },
      'welcomeVideoUpload.uploadedBy': uploadedBy,
    });
  }

  /**
   * Records a welcome video that never arrived — the coach cancelled, or the SDK gave up. FAILED, as
   * a long session records it, so the server's stages leave it alone and the coach can try again.
   *
   * @param {Object} params
   * @param {string} params.cause One of `LongSessionFailureCause` — CANCELLED or UPLOAD_ERROR.
   * @param {string} [params.message] The SDK's own message, when there was one.
   * @param {string} [params.cancelledBy] Who stopped it.
   * @returns {Promise<void>}
   */
  markWelcomeVideoUploadStopped({ cause, message = '', cancelledBy }) {
    const error = {
      stage: LongSessionStage.UPLOADING,
      cause,
      message,
      ...(cancelledBy ? { cancelledBy } : {}),
    };
    return this.updateFields({
      'welcomeVideoUpload.publishStatus': WorkoutPublishStatus.FAILED,
      'welcomeVideoUpload.videoProcessing': {
        stage: LongSessionStage.UPLOADING,
        error,
        updatedAt: getFirebase().firestore.FieldValue.serverTimestamp(),
      },
    });
  }

  getFeaturesConfig = () => {
    const configByID = {
      [FeatureId.NUTRITION_PLAN]: this.isMealPlanEnabled,
      [FeatureId.WORKOUT_VIDEO_FEEDBACK]: this.isVideoFeedBackEnabled,
      [FeatureId.ASSISTANT_COACH]: this.isAssistantCoachEnabled,
      [FeatureId.CHAT]: this.isChatEnabled,
      [FeatureId.CHECK_IN_NOTIFICATION]: this.isCheckInFeedBackEnabled,
      [FeatureId.AI_FRIEND]: this.isAiFriendEnabled,
    };

    const config = Object.entries(configByID).reduce((acc, [id, isEnabled]) => {
      if (isEnabled) {
        acc.enabled.push(id);
      } else {
        acc.disabled.push(id);
      }
      return acc;
    }, { enabled: [], disabled: [] });

    return config;
  }

  async getPlans() {
    const priceCollection = new Collection(`${this.path}/price`, {
      createDocument: (source, options) => new Plan(source, options),
    });

    await priceCollection.fetch();

    return priceCollection;
  }

  async getPlanById(planId) {
    const priceDoc = new Plan(`${this.path}/price/${planId}`);

    await priceDoc.init();

    return priceDoc;
  }

  static async getProductByCoachId(coachId) {
    const productCollection = new Collection(firestorePaths.PRODUCT, {
      createDocument: ({ id }, options) => new Product(id, options),
      query: (query) => query
        .where('coach', '==', coachId)
      ,
    });
    await Product.initCollection(productCollection);

    return productCollection;
  }

  static async getAllProducts() {
    const productCollection = new Collection(firestorePaths.PRODUCT, {
      createDocument: ({ id }, options) => new Product(id, { ...options, disableObserverRefCount: true }),
    });
    await Product.initCollection(productCollection);
    return productCollection;
  }

  async getPackages() {
    const packageCollection = new Collection(`${this.path}/package`, {
      createDocument: (source, options) => new Package(source, options),
    });

    await packageCollection.fetch();
    return packageCollection;
  }

  static async getById(id) {
    const productDoc = new Product(id);
    await productDoc.init();
    return productDoc.exists ? productDoc : null;
  }
}

export default Product;
export {
  FeatureId,
  ProductType,
};
